import React, { useState, useEffect } from "react";
import { Spinner } from "react-bootstrap";
import { useHistory, useLocation } from "react-router-dom";
import axiosInstance from "../../../services/AxiosInstance";
import swal from "sweetalert";
import PageTitle from "../../layouts/PageTitle";

// Parse ?channelId=...&channelName=... from URL
const useQuery = () => new URLSearchParams(useLocation().search);

const ChannelSchedulePost = () => {
  const query       = useQuery();
  const history     = useHistory();
  const [planInfo, setPlanInfo]         = useState(null);
  const [channels, setChannels]         = useState([]);
  const [loading, setLoading]           = useState(true);
  const [saving, setSaving]             = useState(false);

  const [channelId, setChannelId]       = useState(query.get("channelId") || "");
  const [text, setText]                 = useState("");
  const [mediaType, setMediaType]       = useState(""); // "" | image | video | document | poll
  const [mediaFile, setMediaFile]       = useState(null);
  const [pollQuestion, setPollQuestion] = useState("");
  const [pollOptions, setPollOptions]   = useState(["", ""]);
  const [scheduledDate, setScheduledDate] = useState("");
  const [scheduledTime, setScheduledTime] = useState("");
  const [isRecurring, setIsRecurring]   = useState(false);
  const [recurringType, setRecurringType] = useState("daily");
  const [postingMode, setPostingMode]   = useState("auto");

  // Min datetime: now + 5 min
  const minDateTime = () => {
    const d = new Date(Date.now() + 5 * 60 * 1000);
    return d.toISOString().slice(0, 16);
  };

  // Max datetime: now + maxScheduleDaysAhead days
  const maxDateTime = () => {
    if (!planInfo?.maxScheduleDaysAhead) return "";
    const d = new Date(Date.now() + planInfo.maxScheduleDaysAhead * 24 * 60 * 60 * 1000);
    return d.toISOString().slice(0, 16);
  };

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const [planRes, chRes] = await Promise.all([
          axiosInstance.get("channels/plan-info"),
          axiosInstance.get("channels"),
        ]);
        setPlanInfo(planRes.data);
        if (chRes.data.status) setChannels(chRes.data.data || []);
      } catch {}
      setLoading(false);
    };
    load();
  }, []);

  const addPollOption = () => {
    if (pollOptions.length < 12) setPollOptions([...pollOptions, ""]);
  };

  const updatePollOption = (i, val) => {
    const updated = [...pollOptions];
    updated[i] = val;
    setPollOptions(updated);
  };

  const removePollOption = (i) => {
    if (pollOptions.length <= 2) return;
    setPollOptions(pollOptions.filter((_, idx) => idx !== i));
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!channelId) return swal("Error", "Please select a channel", "error");
    if (!scheduledDate || !scheduledTime) return swal("Error", "Please select date and time", "error");

    const scheduledAt = new Date(`${scheduledDate}T${scheduledTime}`);
    if (scheduledAt <= new Date()) return swal("Error", "Scheduled time must be in the future", "error");

    if (mediaType === "poll") {
      const validOptions = pollOptions.filter(o => o.trim());
      if (!pollQuestion.trim()) return swal("Error", "Poll question is required", "error");
      if (validOptions.length < 2) return swal("Error", "Poll needs at least 2 options", "error");
    }

    setSaving(true);
    try {
      const formData = new FormData();
      formData.append("channelId",   channelId);
      formData.append("text",        text);
      formData.append("scheduledAt", scheduledAt.toISOString());
      formData.append("isRecurring", isRecurring ? "true" : "false");
      formData.append("recurringType", recurringType);
      formData.append("postingMode", postingMode);
      if (mediaType) formData.append("mediaType", mediaType);

      if (mediaType === "poll") {
        formData.append("pollQuestion", pollQuestion);
        formData.append("pollOptions",  JSON.stringify(pollOptions.filter(o => o.trim())));
      } else if (mediaFile) {
        formData.append("media", mediaFile);
      }

      const { data } = await axiosInstance.post("channels/posts", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      if (data.status) {
        swal("Scheduled!", "Your post has been scheduled successfully.", "success").then(() => {
          history.push(`/channel-posts?channelId=${channelId}`);
        });
      } else {
        swal("Error", data.message || "Failed to schedule post", "error");
      }
    } catch (err) {
      swal("Error", err?.response?.data?.message || err.message || "Server error", "error");
    }
    setSaving(false);
  };

  if (!loading && planInfo && !planInfo.enabled) {
    return (
      <>
        <PageTitle activeMenu="Schedule Post" motherMenu="Channel Scheduler" />
        <div className="card p-5 text-center">
          <h5>Channel Scheduler not available on your plan.</h5>
          <a href="/packs" className="btn btn-primary mt-3" style={{ maxWidth: 180, margin: "12px auto 0" }}>Upgrade</a>
        </div>
      </>
    );
  }

  const selectedChannel = channels.find(c => c._id === channelId);

  return (
    <>
      <PageTitle activeMenu="Schedule Post" motherMenu="Channel Scheduler" />
      {loading ? (
        <div className="text-center py-5"><Spinner animation="border" variant="primary" /></div>
      ) : (
        <div className="row">
          <div className="col-lg-8">
            <div className="card">
              <div className="card-header">
                <h4 className="card-title mb-0">Schedule a Channel Post</h4>
              </div>
              <div className="card-body">
                <form onSubmit={onSubmit}>

                  {/* Channel select */}
                  <div className="form-group mb-3">
                    <label className="text-black font-w500">Channel *</label>
                    <select className="form-control" value={channelId} onChange={e => setChannelId(e.target.value)} required>
                      <option value="">-- Select Channel --</option>
                      {channels.map(ch => (
                        <option key={ch._id} value={ch._id}>{ch.name}</option>
                      ))}
                    </select>
                    {channels.length === 0 && (
                      <small className="text-muted">No channels added yet. <a href="/channel-manager">Add a channel first.</a></small>
                    )}
                  </div>

                  {/* Post text */}
                  <div className="form-group mb-3">
                    <label className="text-black font-w500">Post Text</label>
                    <textarea
                      className="form-control"
                      rows={4}
                      placeholder="Write your channel post…"
                      value={text}
                      onChange={e => setText(e.target.value)}
                    />
                  </div>

                  {/* Media type */}
                  <div className="form-group mb-3">
                    <label className="text-black font-w500">Attach Media (optional)</label>
                    <select className="form-control" value={mediaType} onChange={e => { setMediaType(e.target.value); setMediaFile(null); }}>
                      <option value="">Text only</option>
                      {planInfo?.allowImage    && <option value="image">Image</option>}
                      {planInfo?.allowVideo    && <option value="video">Video</option>}
                      {planInfo?.allowDocument && <option value="document">Document / PDF</option>}
                      {planInfo?.allowPoll     && <option value="poll">Poll</option>}
                    </select>
                  </div>

                  {/* File upload */}
                  {["image","video","document"].includes(mediaType) && (
                    <div className="form-group mb-3">
                      <label className="text-black font-w500">Upload File *</label>
                      <input
                        type="file"
                        className="form-control"
                        accept={
                          mediaType === "image"    ? "image/*" :
                          mediaType === "video"    ? "video/*" :
                          ".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx"
                        }
                        onChange={e => setMediaFile(e.target.files[0] || null)}
                        required
                      />
                    </div>
                  )}

                  {/* Poll */}
                  {mediaType === "poll" && (
                    <div className="border rounded p-3 mb-3" style={{ background: "#f8f9fa" }}>
                      <label className="text-black font-w500 d-block mb-2">Poll Question *</label>
                      <input
                        type="text"
                        className="form-control mb-3"
                        placeholder="What would you like to ask?"
                        value={pollQuestion}
                        onChange={e => setPollQuestion(e.target.value)}
                        required
                      />
                      <label className="text-black font-w500 d-block mb-2">Options (min 2)</label>
                      {pollOptions.map((opt, i) => (
                        <div className="d-flex gap-2 mb-2" key={i}>
                          <input
                            type="text"
                            className="form-control"
                            placeholder={`Option ${i + 1}`}
                            value={opt}
                            onChange={e => updatePollOption(i, e.target.value)}
                          />
                          {pollOptions.length > 2 && (
                            <button type="button" className="btn btn-outline-danger btn-sm" onClick={() => removePollOption(i)}>✕</button>
                          )}
                        </div>
                      ))}
                      {pollOptions.length < 12 && (
                        <button type="button" className="btn btn-outline-secondary btn-sm mt-1" onClick={addPollOption}>+ Add Option</button>
                      )}
                    </div>
                  )}

                  {/* Date + Time */}
                  <div className="row mb-3">
                    <div className="form-group col-md-6">
                      <label className="text-black font-w500">Date *</label>
                      <input
                        type="date"
                        className="form-control"
                        value={scheduledDate}
                        min={new Date().toISOString().split("T")[0]}
                        onChange={e => setScheduledDate(e.target.value)}
                        required
                      />
                    </div>
                    <div className="form-group col-md-6">
                      <label className="text-black font-w500">Time *</label>
                      <input
                        type="time"
                        className="form-control"
                        value={scheduledTime}
                        onChange={e => setScheduledTime(e.target.value)}
                        required
                      />
                    </div>
                  </div>

                  {/* Recurring */}
                  {planInfo?.allowRecurring && (
                    <div className="form-group mb-3">
                      <div className="d-flex align-items-center gap-3">
                        <label className="text-black font-w500 mb-0">Recurring Post</label>
                        <div className="form-check form-switch mb-0">
                          <input
                            className="form-check-input"
                            type="checkbox"
                            checked={isRecurring}
                            onChange={e => setIsRecurring(e.target.checked)}
                          />
                        </div>
                      </div>
                      {isRecurring && (
                        <select className="form-control mt-2" value={recurringType} onChange={e => setRecurringType(e.target.value)}>
                          <option value="daily">Daily</option>
                          <option value="weekly">Weekly</option>
                          <option value="monthly">Monthly</option>
                        </select>
                      )}
                    </div>
                  )}

                  {/* Posting mode */}
                  <div className="form-group mb-4">
                    <label className="text-black font-w500">Posting Mode</label>
                    <select className="form-control" value={postingMode} onChange={e => setPostingMode(e.target.value)}>
                      <option value="auto">Auto — Post automatically at scheduled time</option>
                      <option value="manual">Manual — Notify me to confirm before posting</option>
                    </select>
                  </div>

                  <div className="d-flex gap-2">
                    <button type="submit" className="btn btn-primary" disabled={saving}>
                      {saving ? <Spinner animation="border" size="sm" /> : "Schedule Post"}
                    </button>
                    <button type="button" className="btn btn-outline-secondary" onClick={() => history.push("/channel-manager")}>
                      Cancel
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>

          {/* Sidebar info */}
          <div className="col-lg-4">
            <div className="card">
              <div className="card-body">
                <h6 className="text-primary mb-3">Your Plan Limits</h6>
                {planInfo && (
                  <ul className="list-unstyled small text-muted">
                    <li>✓ Max channels: <strong>{planInfo.maxChannels}</strong></li>
                    <li>✓ Posts/channel/day: <strong>{planInfo.maxPostsPerChannelPerDay || "Unlimited"}</strong></li>
                    <li>✓ Posts/channel/month: <strong>{planInfo.maxPostsPerChannelPerMonth || "Unlimited"}</strong></li>
                    <li>✓ Schedule up to: <strong>{planInfo.maxScheduleDaysAhead} days ahead</strong></li>
                    <li>✓ Image: <strong>{planInfo.allowImage ? "Yes" : "No"}</strong></li>
                    <li>✓ Video: <strong>{planInfo.allowVideo ? "Yes" : "No"}</strong></li>
                    <li>✓ Document: <strong>{planInfo.allowDocument ? "Yes" : "No"}</strong></li>
                    <li>✓ Poll: <strong>{planInfo.allowPoll ? "Yes" : "No"}</strong></li>
                  </ul>
                )}
              </div>
            </div>
            {selectedChannel && (
              <div className="card">
                <div className="card-body">
                  <h6 className="text-primary mb-2">Channel Usage Today</h6>
                  <p className="mb-1 small">Posts today: <strong>{selectedChannel.postsToday ?? 0}</strong>
                    {planInfo?.maxPostsPerChannelPerDay > 0 ? ` / ${planInfo.maxPostsPerChannelPerDay}` : ""}
                  </p>
                  <p className="mb-0 small">Posts this month: <strong>{selectedChannel.postsThisMonth ?? 0}</strong>
                    {planInfo?.maxPostsPerChannelPerMonth > 0 ? ` / ${planInfo.maxPostsPerChannelPerMonth}` : ""}
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default ChannelSchedulePost;
